import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Orbit, Clock } from 'lucide-react';
import { getAllSets, getSetCards } from '../services/pokemonTcgApi';
import { useNavigate } from 'react-router-dom';
import { slugify } from '../utils/slugify';
import type { PokemonSet } from '../types';
import Logo from '../components/Logo';
import CardModal from '../components/CardModal';
import { usePokeverseCursors } from '../hooks/usePokeverseCursors';
import { PresenceCursors, ActiveUsersIndicator } from '../components/PresenceCursors';
import { PokeverseIconAnimated } from '../components/icons/PokeverseIcon';
import { useTypingEffect } from '../hooks/useTypingEffect';
import PokeverseSkyBackground from '../components/PokeverseSkyBackground';

// 3D Card shine animation styles
const card3DStyles = `
  @keyframes shine {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  
  @keyframes holoShine {
    0% { 
      background-position: 0% 50%;
      filter: hue-rotate(0deg);
    }
    50% { 
      background-position: 100% 50%;
      filter: hue-rotate(180deg);
    }
    100% { 
      background-position: 0% 50%;
      filter: hue-rotate(360deg);
    }
  }
`;

// Card type with images
interface Card {
  id: string;
  name: string;
  number: string;
  set_id?: string;
  rarity?: string;
  supertype?: string;
  hp?: string;
  types?: string[];
  abilities?: Array<{
    name: string;
    text: string;
    type: string;
  }>;
  attacks?: Array<{
    name: string;
    cost: string[];
    damage: string;
    text?: string;
  }>;
  weaknesses?: Array<{
    type: string;
    value: string;
  }>;
  retreatCost?: number;
  retreat_cost?: string[];
  flavorText?: string;
  images?: {
    small?: string;
    large?: string;
  };
  set?: {
    id?: string;
    name: string;
    series: string;
    releaseDate: string;
    images?: {
      symbol?: string;
      logo?: string;
    };
  };
  // TCGPlayer prices
  tcgplayer_url?: string;
  tcgplayer_updated_at?: string;
  tcg_normal_low?: number;
  tcg_normal_mid?: number;
  tcg_normal_high?: number;
  tcg_normal_market?: number;
  tcg_holofoil_low?: number;
  tcg_holofoil_mid?: number;
  tcg_holofoil_high?: number;
  tcg_holofoil_market?: number;
  tcg_reverse_holofoil_low?: number;
  tcg_reverse_holofoil_mid?: number;
  tcg_reverse_holofoil_high?: number;
  tcg_reverse_holofoil_market?: number;
  // Cardmarket prices
  cardmarket_url?: string;
  cardmarket_updated_at?: string;
  cardmarket_avg_sell_price?: number;
  cardmarket_low_price?: number;
  cardmarket_trend_price?: number;
}

interface SeriesNode {
  id: string;
  name: string;
  sets: PokemonSet[];
  color: string;
  logoUrl?: string;
  symbolUrl?: string;
}

interface SetNode {
  id: string;
  name: string;
  series: string;
  total: number;
  releaseDate: string;
  images?: {
    symbol?: string;
    logo?: string;
  };
}

type ViewLevel = 'series' | 'sets' | 'cards';

interface ViewState {
  level: ViewLevel;
  selectedSeries?: SeriesNode;
  selectedSet?: SetNode;
  cards?: Card[];
}

// Generate a color for each series based on its name
const getSeriesColor = (seriesName: string): string => {
  const colors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
    '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B739', '#52B788',
    '#E63946', '#A8DADC', '#457B9D', '#E76F51', '#2A9D8F',
    '#F4A261', '#E9C46A', '#8338EC', '#FF006E', '#3A86FF'
  ];
  
  // Create a hash from the series name
  const hash = seriesName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};



export default function PokéversePage() {
  const [viewState, setViewState] = useState<ViewState>({ level: 'series' });
  const [seriesData, setSeriesData] = useState<SeriesNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'galaxy' | 'timeline'>('galaxy');
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const navigate = useNavigate();
  
  // Real-time cursor presence
  const { cursors, updateCursor, isConnected, activeUsers } = usePokeverseCursors();

  // Animated typing effect for subtitle
  const typingText = useTypingEffect([
    "Welcome to the Pokéverse, collector!",
    "Explore all Pokémon TCG series...",
    "Discover rare cards and hidden gems...",
    "Join fellow collectors in real-time!",
    "Your journey through Pokémon history awaits...",
    "Over 19,000 cards to explore...",
  ], 60, 30, 2500);

  // Disable body scroll when component is mounted
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Load all series on mount
  useEffect(() => {
    const loadSeries = async () => {
      try {
        const allSets = await getAllSets();
        
        // Group sets by series
        const seriesMap = new Map<string, PokemonSet[]>();
        allSets.forEach(set => {
          if (!seriesMap.has(set.series)) {
            seriesMap.set(set.series, []);
          }
          seriesMap.get(set.series)!.push(set);
        });
        
        // Map of series names to their representative set IDs (the "base" set of each series)
        const SERIES_REPRESENTATIVE_SETS: Record<string, string> = {
          'Scarlet & Violet': 'sv1',           // Scarlet & Violet base set
          'Sword & Shield': 'swsh1',           // Sword & Shield base set
          'Sun & Moon': 'sm1',                 // Sun & Moon base set
          'XY': 'xy1',                         // XY base set
          'Black & White': 'bw1',              // Black & White base set
          'HeartGold & SoulSilver': 'hgss1',   // HeartGold & SoulSilver base set
          'Platinum': 'pl1',                   // Platinum base set
          'Diamond & Pearl': 'dp1',            // Diamond & Pearl base set
          'EX': 'ex1',                         // EX Ruby & Sapphire
          'e-Card': 'ecard1',                  // Expedition Base Set
          'Neo': 'neo1',                       // Neo Genesis
          'Base': 'base1',                     // Base Set
          'Gym': 'gym1',                       // Gym Heroes
          'Other': 'np',                       // Nintendo Black Star Promos
          'POP': 'pop1',                       // POP Series 1
        };

        // Convert to SeriesNode array
        const series: SeriesNode[] = Array.from(seriesMap.entries()).map(([name, sets]) => {
          // Find the representative set for this series
          const representativeSetId = SERIES_REPRESENTATIVE_SETS[name];
          let representativeSet = representativeSetId 
            ? sets.find(s => s.id === representativeSetId) 
            : null;
          
          // Fallback: Sort sets by release date and use the oldest (first) set
          if (!representativeSet) {
            const sortedSets = [...sets].sort((a, b) => 
              new Date(a.releaseDate).getTime() - new Date(b.releaseDate).getTime()
            );
            representativeSet = sortedSets[0];
          }
          
          return {
            id: slugify(name),
            name,
            sets,
            color: getSeriesColor(name),
            logoUrl: representativeSet?.images?.logo,
            symbolUrl: representativeSet?.images?.symbol,
          };
        });
        
        setSeriesData(series);
        setLoading(false);
      } catch (error) {
        console.error('Error loading series:', error);
        setLoading(false);
      }
    };
    
    loadSeries();
  }, []);

  // Handle clicking on a series
  const handleSeriesClick = (series: SeriesNode, fromTimeline: boolean = false) => {
    if (fromTimeline) {
      setViewMode('timeline');
    }
    setViewState({
      level: 'sets',
      selectedSeries: series,
    });
  };

  // Handle clicking on a set
  const handleSetClick = async (set: SetNode) => {
    setLoading(true);
    try {
      const cards = await getSetCards(set.id);
      setViewState({
        ...viewState,
        level: 'cards',
        selectedSet: set,
        cards,
      });
    } catch (error) {
      console.error('Error loading cards:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle clicking on a card
  const handleCardClick = (card: Card) => {
    setSelectedCard(card);
  };

  // Handle closing the modal
  const handleCloseModal = () => {
    setSelectedCard(null);
  };

  // Handle selecting a different card from similar cards - navigate to it in Pokeverse
  const handleCardSelect = async (card: any) => {
    console.log('Navigating to card:', card);
    
    // Close the modal first
    setSelectedCard(null);
    
    // Find the series that contains this card's set
    const cardSeries = card.set?.series || '';
    // Try to get set ID from multiple possible locations
    const cardSetId = card.set?.id || card.set_id || '';
    
    console.log('Looking for series:', cardSeries, 'and set:', cardSetId);
    
    // Find the matching series in our data
    const matchingSeries = seriesData.find(s => 
      s.name.toLowerCase() === cardSeries.toLowerCase()
    );
    
    if (matchingSeries) {
      console.log('Found matching series:', matchingSeries.name);
      
      // Navigate to the series first
      setViewState({
        level: 'sets',
        selectedSeries: matchingSeries,
      });
      
      // Then navigate to the set
      const matchingSet = matchingSeries.sets.find(s => s.id === cardSetId);
      if (matchingSet) {
        console.log('Found matching set:', matchingSet.name);
        setLoading(true);
        try {
          const cards = await getSetCards(matchingSet.id);
          setViewState({
            level: 'cards',
            selectedSeries: matchingSeries,
            selectedSet: matchingSet,
            cards,
          });
        } catch (error) {
          console.error('Error loading cards:', error);
        } finally {
          setLoading(false);
        }
      } else {
        console.log('Could not find matching set with ID:', cardSetId);
      }
    } else {
      console.log('Could not find matching series');
    }
    
    // Reset drag and zoom
    setDragOffset({ x: 0, y: 0 });
    setZoom(1);
  };

  // Handle back navigation
  const handleBack = () => {
    // Reset drag offset and zoom when going back
    setDragOffset({ x: 0, y: 0 });
    setZoom(1);
    
    if (viewState.level === 'cards') {
      setViewState({
        level: 'sets',
        selectedSeries: viewState.selectedSeries,
      });
    } else if (viewState.level === 'sets') {
      setViewState({
        level: 'series',
      });
    }
  };

  // Drag handlers - Mouse
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y });
  };

  // Cache container rect for performance
  const containerRectRef = useRef<DOMRect | null>(null);
  
  // Update cached rect periodically
  useEffect(() => {
    const updateRect = () => {
      if (containerRef.current) {
        containerRectRef.current = containerRef.current.getBoundingClientRect();
      }
    };
    updateRect();
    const interval = setInterval(updateRect, 100); // Update every 100ms
    return () => clearInterval(interval);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging) {
      setDragOffset({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
    
    // Update cursor position for presence using cached rect
    const rect = containerRectRef.current;
    if (rect) {
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      updateCursor({ x, y, viewLevel: viewState.level, viewMode });
    }
  }, [isDragging, dragStart, updateCursor, viewState.level, viewMode]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Touch handlers for mobile drag
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const initialPinchDistanceRef = useRef<number | null>(null);
  const initialZoomRef = useRef<number>(1);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      // Single touch - drag
      const touch = e.touches[0];
      touchStartRef.current = { x: touch.clientX - dragOffset.x, y: touch.clientY - dragOffset.y };
      setIsDragging(true);
    } else if (e.touches.length === 2) {
      // Two finger - pinch to zoom
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      initialPinchDistanceRef.current = Math.sqrt(dx * dx + dy * dy);
      initialZoomRef.current = zoom;
    }
  }, [dragOffset, zoom]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1 && touchStartRef.current) {
      // Single touch drag
      const touch = e.touches[0];
      setDragOffset({
        x: touch.clientX - touchStartRef.current.x,
        y: touch.clientY - touchStartRef.current.y,
      });
    } else if (e.touches.length === 2 && initialPinchDistanceRef.current) {
      // Pinch to zoom
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const currentDistance = Math.sqrt(dx * dx + dy * dy);
      const scale = currentDistance / initialPinchDistanceRef.current;
      const newZoom = Math.min(Math.max(initialZoomRef.current * scale, 0.5), 3);
      setZoom(newZoom);
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    touchStartRef.current = null;
    initialPinchDistanceRef.current = null;
    setIsDragging(false);
  }, []);

  // Zoom handler ref for non-passive event listener
  const containerRef = useRef<HTMLDivElement>(null);

  // Set up wheel event listener with passive: false to allow preventDefault
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const zoomSpeed = 0.001;
      const minZoom = 0.5;
      const maxZoom = 3;
      
      // Calculate new zoom level
      const delta = -e.deltaY * zoomSpeed;
      setZoom(prevZoom => Math.min(Math.max(prevZoom + delta, minZoom), maxZoom));
    };

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden relative">
      {/* 3D Card Animation Styles */}
      <style>{card3DStyles}</style>
      
      {/* Animated Sky Background */}
      <PokeverseSkyBackground />
      
      {/* Real-time Cursors */}
      <PresenceCursors 
        cursors={cursors} 
        containerRef={containerRef} 
        currentViewLevel={viewState.level}
        currentViewMode={viewMode}
      />
      
      {/* Header */}
      <div className="fixed top-0 lg:left-64 left-14 right-0 z-40 p-4 bg-gradient-to-b from-black to-transparent">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Back button - hidden on mobile, shown on desktop */}
            {viewState.level !== 'series' && (
              <button
                onClick={handleBack}
                className="hidden sm:flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              >
                <ArrowLeft size={20} />
                Back
              </button>
            )}
            <div>
              <h1 className="text-lg sm:text-2xl font-bold flex items-center gap-2">
                <PokeverseIconAnimated size={24} />
                Pokéverse
              </h1>
              <p className="text-xs sm:text-sm text-white/60 min-w-[200px] sm:min-w-[280px]">
                {viewState.level === 'series' && (
                  <span>
                    {typingText}
                    <span className="animate-pulse">|</span>
                  </span>
                )}
                {viewState.level === 'sets' && `${viewState.selectedSeries?.name} Sets`}
                {viewState.level === 'cards' && `${viewState.selectedSet?.name}`}
              </p>
            </div>
          </div>
          
          {/* View Mode Toggle - Show on series and sets levels */}
          {(viewState.level === 'series' || viewState.level === 'sets') && (
            <div className="flex items-center gap-2 mr-4">
              <button
                onClick={() => setViewMode('galaxy')}
                className={`p-2 rounded-lg transition-all ${
                  viewMode === 'galaxy' 
                    ? 'bg-white/20 text-white' 
                    : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/80'
                }`}
                title="Galaxy View"
              >
                <Orbit size={18} />
              </button>
              <button
                onClick={() => setViewMode('timeline')}
                className={`p-2 rounded-lg transition-all ${
                  viewMode === 'timeline' 
                    ? 'bg-white/20 text-white' 
                    : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/80'
                }`}
                title="Timeline View"
              >
                <Clock size={18} />
              </button>
            </div>
          )}
          
          {/* Active Users Indicator */}
          <ActiveUsersIndicator count={activeUsers} isConnected={isConnected} />
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center z-40 bg-black/50 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-white/20 border-t-white"></div>
            <p className="text-white/60">Loading...</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div 
        ref={containerRef}
        className={`pt-24 pb-8 ${viewMode === 'galaxy' ? 'touch-none' : ''}`}
        onMouseDown={viewMode === 'galaxy' ? handleMouseDown : undefined}
        onMouseMove={viewMode === 'galaxy' ? handleMouseMove : undefined}
        onMouseUp={viewMode === 'galaxy' ? handleMouseUp : undefined}
        onMouseLeave={viewMode === 'galaxy' ? handleMouseLeave : undefined}
        onTouchStart={viewMode === 'galaxy' ? handleTouchStart : undefined}
        onTouchMove={viewMode === 'galaxy' ? handleTouchMove : undefined}
        onTouchEnd={viewMode === 'galaxy' ? handleTouchEnd : undefined}
        style={{ cursor: viewMode === 'galaxy' ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
      >
        <AnimatePresence mode="wait">
          {/* Level 1: Series World */}
          {viewState.level === 'series' && viewMode === 'galaxy' && (
            <SeriesWorld
              key="series-galaxy"
              series={seriesData}
              onSeriesClick={handleSeriesClick}
              hoveredNode={hoveredNode}
              setHoveredNode={setHoveredNode}
              dragOffset={dragOffset}
              zoom={zoom}
            />
          )}
          
          {/* Level 1: Timeline View */}
          {viewState.level === 'series' && viewMode === 'timeline' && (
            <TimelineWorld
              key="series-timeline"
              series={seriesData}
              onSeriesClick={handleSeriesClick}
              hoveredNode={hoveredNode}
              setHoveredNode={setHoveredNode}
            />
          )}

          {/* Level 2: Sets in Series - Galaxy View */}
          {viewState.level === 'sets' && viewState.selectedSeries && viewMode === 'galaxy' && (
            <SetsWorld
              key="sets-galaxy"
              series={viewState.selectedSeries}
              onSetClick={handleSetClick}
              onBack={handleBack}
              hoveredNode={hoveredNode}
              setHoveredNode={setHoveredNode}
              dragOffset={dragOffset}
              zoom={zoom}
            />
          )}
          
          {/* Level 2: Sets in Series - Timeline View */}
          {viewState.level === 'sets' && viewState.selectedSeries && viewMode === 'timeline' && (
            <TimelineSetsWorld
              key="sets-timeline"
              series={viewState.selectedSeries}
              onSetClick={handleSetClick}
              onBack={handleBack}
              hoveredNode={hoveredNode}
              setHoveredNode={setHoveredNode}
            />
          )}

          {/* Level 3: Cards in Set */}
          {viewState.level === 'cards' && viewState.cards && (
            <CardsWorld
              key="cards"
              cards={viewState.cards}
              onCardClick={handleCardClick}
              onBack={handleBack}
              hoveredNode={hoveredNode}
              setHoveredNode={setHoveredNode}
              dragOffset={dragOffset}
              zoom={zoom}
            />
          )}
        </AnimatePresence>
      </div>

      {/* Card Detail Modal */}
      <CardModal card={selectedCard} onClose={handleCloseModal} onCardSelect={handleCardSelect} />
    </div>
  );
}

// Series World Component
function SeriesWorld({
  series,
  onSeriesClick,
  hoveredNode,
  setHoveredNode,
  dragOffset,
  zoom,
}: {
  series: SeriesNode[];
  onSeriesClick: (series: SeriesNode) => void;
  hoveredNode: string | null;
  setHoveredNode: (id: string | null) => void;
  dragOffset: { x: number; y: number };
  zoom: number;
}) {
  const containerSize = typeof window !== 'undefined' 
    ? (() => {
        // On desktop (lg), account for sidebar width (256px)
        const availableWidth = window.innerWidth >= 1024 
          ? window.innerWidth - 256 
          : window.innerWidth;
        const availableHeight = window.innerHeight - 120; // Account for header
        return Math.min(availableWidth * 0.98, availableHeight * 0.95);
      })()
    : 1000;
  const logoSize = 180; // Size of the center logo
  const minRadius = 200; // Minimum radius from center
  const radiusStep = 180; // Distance between rings
  const itemSize = 110; // Size of series circles
  const itemSpacing = 30; // Space between items
  
  // Calculate how many items fit in each ring based on its circumference
  const calculateItemsPerRing = (radius: number) => {
    const circumference = 2 * Math.PI * radius;
    const itemArcLength = itemSize + itemSpacing;
    return Math.floor(circumference / itemArcLength);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 1.2 }}
      transition={{ duration: 0.6 }}
      className="relative w-full h-[calc(100vh-120px)] flex items-center justify-center"
    >
      <div 
        className="relative" 
        style={{ 
          width: containerSize, 
          height: containerSize,
          transform: `translate(${dragOffset.x}px, ${dragOffset.y}px) scale(${zoom})`,
          transition: 'transform 0.1s ease-out',
        }}
      >
        {/* Connection Lines */}
        <svg className="absolute inset-0 pointer-events-none z-0" style={{ overflow: 'visible' }}>
          <g transform={`translate(${containerSize / 2}, ${containerSize / 2})`}>
            {(() => {
              let itemIndex = 0;
              let currentRing = 0;
              const lines = [];
              
              while (itemIndex < series.length) {
                const ringRadius = minRadius + (currentRing * radiusStep);
                const itemsInThisRing = calculateItemsPerRing(ringRadius);
                
                for (let i = 0; i < itemsInThisRing && itemIndex < series.length; i++) {
                  const totalInRing = Math.min(itemsInThisRing, series.length - itemIndex + i);
                  const angle = (i / totalInRing) * Math.PI * 2 - Math.PI / 2;
                  const x = Math.cos(angle) * ringRadius;
                  const y = Math.sin(angle) * ringRadius;
                  
                  lines.push(
                    <line
                      key={`line-${itemIndex}`}
                      x1={0}
                      y1={0}
                      x2={x}
                      y2={y}
                      stroke="rgba(255, 255, 255, 0.1)"
                      strokeWidth="1"
                    />
                  );
                  itemIndex++;
                }
                currentRing++;
              }
              
              return lines;
            })()}
          </g>
        </svg>

        {/* Series Circles */}
        {(() => {
          let itemIndex = 0;
          let currentRing = 0;
          const ringData: Array<{ ring: number; posInRing: number; totalInRing: number }> = [];
          
          // Pre-calculate which ring each series belongs to
          while (itemIndex < series.length) {
            const ringRadius = minRadius + (currentRing * radiusStep);
            const itemsInThisRing = calculateItemsPerRing(ringRadius);
            
            for (let i = 0; i < itemsInThisRing && itemIndex < series.length; i++) {
              ringData.push({
                ring: currentRing,
                posInRing: i,
                totalInRing: Math.min(itemsInThisRing, series.length - itemIndex + i)
              });
              itemIndex++;
            }
            currentRing++;
          }
          
          return series.map((seriesNode, index) => {
            const { ring, posInRing, totalInRing } = ringData[index];
            
            // Calculate radius for this ring
            const ringRadius = minRadius + (ring * radiusStep);
            
            // Calculate angle for position in circle
            const angle = (posInRing / totalInRing) * Math.PI * 2 - Math.PI / 2;
            const pos = {
              x: Math.cos(angle) * ringRadius,
              y: Math.sin(angle) * ringRadius,
            };
            
            const isHovered = hoveredNode === seriesNode.id;
            const size = 110; // Fixed size for all circles

          return (
            <motion.div
              key={seriesNode.id}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ 
                opacity: 1, 
                scale: isHovered ? 1.3 : 1,
                x: pos.x,
                y: pos.y,
                zIndex: isHovered ? 100 : 10,
              }}
              transition={{ 
                delay: index * 0.02,
                type: 'spring',
                stiffness: 100,
                damping: 15,
              }}
              className="absolute cursor-pointer"
              style={{
                left: '50%',
                top: '50%',
                width: size,
                height: size,
                marginLeft: -size / 2,
                marginTop: -size / 2,
              }}
              onClick={() => onSeriesClick(seriesNode)}
              onMouseEnter={() => setHoveredNode(seriesNode.id)}
              onMouseLeave={() => setHoveredNode(null)}
            >
              <div
                className="w-full h-full rounded-full flex items-center justify-center shadow-lg hover:shadow-2xl transition-shadow overflow-hidden relative"
                style={{
                  border: isHovered ? `3px solid white` : `2px solid ${seriesNode.color}`,
                }}
              >
                {/* Solid background to cover lines */}
                <div 
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: seriesNode.logoUrl 
                      ? `radial-gradient(circle, ${seriesNode.color}44, ${seriesNode.color}22), #000`
                      : `radial-gradient(circle, ${seriesNode.color}, ${seriesNode.color}aa)`,
                  }}
                />
                <div className="relative z-10 w-full h-full flex items-center justify-center">
                {seriesNode.logoUrl || seriesNode.symbolUrl ? (
                  <div className="relative w-full h-full flex items-center justify-center p-2">
                    <img
                      src={seriesNode.logoUrl || seriesNode.symbolUrl}
                      alt={seriesNode.name}
                      className="max-w-full max-h-full object-contain drop-shadow-lg"
                      style={{
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                      }}
                    />
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="text-xs font-bold text-white drop-shadow-lg">
                      {seriesNode.sets.length}
                    </div>
                  </div>
                )}
                </div>
              </div>
              
              {/* Tooltip */}
              {isHovered && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-black/90 rounded-lg text-sm whitespace-nowrap z-50 pointer-events-none"
                >
                  <div className="font-semibold">{seriesNode.name}</div>
                  <div className="text-white/60 text-xs">{seriesNode.sets.length} sets</div>
                </motion.div>
              )}
            </motion.div>
          );
        })})()}

        {/* Center Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, type: 'spring', stiffness: 100, damping: 15 }}
          className="absolute left-1/2 top-1/2 z-20 cursor-pointer"
          style={{
            width: logoSize,
            height: logoSize,
            marginLeft: -logoSize / 2,
            marginTop: -logoSize / 2,
          }}
          onClick={() => window.location.href = '/'}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <div className="w-full h-full rounded-full flex items-center justify-center shadow-2xl border-2 border-white/20 relative">
            {/* Solid background to cover lines */}
            <div className="absolute inset-0 rounded-full bg-black"></div>
            <div className="absolute inset-0 rounded-full bg-white/10 backdrop-blur-md"></div>
            <Logo className="w-4/5 h-4/5 relative z-10" />
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

// Timeline World Component - Shows series in chronological order
function TimelineWorld({
  series,
  onSeriesClick,
  hoveredNode,
  setHoveredNode,
}: {
  series: SeriesNode[];
  onSeriesClick: (series: SeriesNode, fromTimeline: boolean) => void;
  hoveredNode: string | null;
  setHoveredNode: (id: string | null) => void;
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollButtons = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', updateScrollButtons);
      updateScrollButtons();
      return () => container.removeEventListener('scroll', updateScrollButtons);
    }
  }, [updateScrollButtons]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };
  // Sort series by their earliest set release date
  const sortedSeries = useMemo(() => {
    return [...series].sort((a, b) => {
      const aEarliestDate = Math.min(...a.sets.map(s => new Date(s.releaseDate).getTime()));
      const bEarliestDate = Math.min(...b.sets.map(s => new Date(s.releaseDate).getTime()));
      return aEarliestDate - bEarliestDate;
    }).map((s, index) => {
      const sortedSets = [...s.sets].sort((a, b) => 
        new Date(a.releaseDate).getTime() - new Date(b.releaseDate).getTime()
      );
      const earliestSet = sortedSets[0];
      const latestSet = sortedSets[sortedSets.length - 1];
      const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      };
      return {
        ...s,
        releaseYear: new Date(earliestSet.releaseDate).getFullYear(),
        dateRange: sortedSets.length > 1 
          ? `${formatDate(earliestSet.releaseDate)} - ${formatDate(latestSet.releaseDate)}`
          : formatDate(earliestSet.releaseDate),
        index,
      };
    });
  }, [series]);

  // Get unique years for markers
  const years = useMemo(() => {
    const yearSet = new Set(sortedSeries.map(s => s.releaseYear));
    return Array.from(yearSet).sort((a, b) => a - b);
  }, [sortedSeries]);

  // Group series by year
  const seriesByYear = useMemo(() => {
    const groups: Record<number, typeof sortedSeries> = {};
    sortedSeries.forEach(s => {
      if (!groups[s.releaseYear]) groups[s.releaseYear] = [];
      groups[s.releaseYear].push(s);
    });
    return groups;
  }, [sortedSeries]);

  // Handle mouse wheel for horizontal scroll on PC
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (scrollContainerRef.current) {
      e.preventDefault();
      // Multiply scroll amount for faster scrolling (3x sensitivity)
      const scrollMultiplier = 3;
      scrollContainerRef.current.scrollLeft += (e.deltaY + e.deltaX) * scrollMultiplier;
      updateScrollButtons();
    }
  }, [updateScrollButtons]);

  // Drag to scroll
  const [isDraggingTimeline, setIsDraggingTimeline] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [scrollStartX, setScrollStartX] = useState(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (scrollContainerRef.current) {
      setIsDraggingTimeline(true);
      setDragStartX(e.clientX);
      setScrollStartX(scrollContainerRef.current.scrollLeft);
      scrollContainerRef.current.style.scrollBehavior = 'auto';
    }
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDraggingTimeline || !scrollContainerRef.current) return;
    e.preventDefault();
    const dx = e.clientX - dragStartX;
    scrollContainerRef.current.scrollLeft = scrollStartX - dx;
    updateScrollButtons();
  }, [isDraggingTimeline, dragStartX, scrollStartX, updateScrollButtons]);

  const handleMouseUp = useCallback(() => {
    setIsDraggingTimeline(false);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.scrollBehavior = 'smooth';
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (isDraggingTimeline) {
      setIsDraggingTimeline(false);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.style.scrollBehavior = 'smooth';
      }
    }
  }, [isDraggingTimeline]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      transition={{ duration: 0.5 }}
      className="w-full h-[calc(100vh-120px)] relative flex flex-col"
    >
      
      {/* Scroll Left Button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: canScrollLeft ? 1 : 0.3 }}
        onClick={() => scroll('left')}
        disabled={!canScrollLeft}
        className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/80 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all disabled:cursor-not-allowed"
      >
        <ArrowLeft size={20} />
      </motion.button>
      
      {/* Scroll Right Button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: canScrollRight ? 1 : 0.3 }}
        onClick={() => scroll('right')}
        disabled={!canScrollRight}
        className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/80 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all disabled:cursor-not-allowed rotate-180"
      >
        <ArrowLeft size={20} />
      </motion.button>
      
      {/* Bottom info bar */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 bg-black/60 backdrop-blur-sm px-4 py-2 rounded-full border border-white/10">
        <div className="text-white text-sm font-medium">Pokémon TCG Timeline</div>
        <div className="w-px h-4 bg-white/20" />
        <div className="text-white/50 text-xs sm:text-sm">{years[0]} to Present</div>
        <div className="w-px h-4 bg-white/20" />
        <div className="text-white/40 text-xs">← Scroll or drag to explore →</div>
      </div>

      <div 
        ref={scrollContainerRef}
        className={`flex-1 overflow-x-auto overflow-y-hidden select-none ${isDraggingTimeline ? 'cursor-grabbing' : 'cursor-grab'}`}
        style={{ scrollBehavior: 'smooth' }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <div className="min-w-max px-8 sm:px-16 py-4 h-full flex flex-col">
        
          {/* Timeline Container */}
          <div className="flex-1 relative">
            {/* Timeline Line */}
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-purple-500/20 via-cyan-500/40 to-purple-500/20 rounded-full transform -translate-y-1/2" />
            
            {/* Glowing effect on the line */}
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-purple-500/0 via-cyan-400/60 to-purple-500/0 rounded-full transform -translate-y-1/2 blur-sm" />
            
            {/* Years and Series */}
            <div className="flex items-center h-full gap-4 px-12">
              {years.map((year, yearIndex) => (
                <div key={year} className="flex items-center">
                  {/* Year Marker */}
                  <div className="flex flex-col items-center mr-4">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: yearIndex * 0.1 }}
                      className="relative"
                    >
                      <div className="w-4 h-4 bg-cyan-400 rounded-full shadow-lg shadow-cyan-400/50" />
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                        <span className="text-cyan-400 font-bold text-lg">{year}</span>
                      </div>
                      <div className="absolute top-6 left-1/2 transform -translate-x-1/2 w-px h-8 bg-gradient-to-b from-cyan-400/50 to-transparent" />
                    </motion.div>
                  </div>
                  
                  {/* Series for this year */}
                  <div className="flex items-center gap-4 sm:gap-6">
                    {seriesByYear[year]?.map((seriesNode, index) => {
                      const isHovered = hoveredNode === seriesNode.id;
                      const totalSets = seriesNode.sets.length;
                      
                      return (
                        <motion.div
                          key={seriesNode.id}
                          initial={{ opacity: 0, scale: 0, y: index % 2 === 0 ? -40 : 40 }}
                          animate={{ 
                            opacity: 1, 
                            scale: isHovered ? 1.1 : 1,
                            y: index % 2 === 0 ? -60 : 60,
                          }}
                          transition={{ 
                            delay: yearIndex * 0.05 + index * 0.03,
                            type: 'spring',
                            stiffness: 100,
                            damping: 15,
                          }}
                        className="relative cursor-pointer group"
                        onClick={() => onSeriesClick(seriesNode, true)}
                        onMouseEnter={() => setHoveredNode(seriesNode.id)}
                        onMouseLeave={() => setHoveredNode(null)}
                      >
                        {/* Connection line to timeline */}
                        <div 
                          className={`absolute left-1/2 w-px bg-gradient-to-b ${
                            index % 2 === 0 
                              ? 'top-full h-12 from-transparent to-white/30' 
                              : 'bottom-full h-12 from-white/30 to-transparent'
                          }`}
                          style={{ transform: 'translateX(-50%)' }}
                        />
                          
                        {/* Series Circle */}
                        <div
                          className={`w-24 h-24 sm:w-32 sm:h-32 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 overflow-hidden relative ${
                            isHovered ? 'shadow-2xl ring-2 ring-white' : ''
                          }`}
                          style={{
                            border: `2px solid ${seriesNode.color}`,
                          }}
                        >
                            <div 
                              className="absolute inset-0 rounded-full"
                              style={{
                                background: seriesNode.logoUrl 
                                  ? `radial-gradient(circle, ${seriesNode.color}44, ${seriesNode.color}22), #000`
                                  : `radial-gradient(circle, ${seriesNode.color}, ${seriesNode.color}aa)`,
                              }}
                            />
                            <div className="relative z-10 w-full h-full flex items-center justify-center p-2">
                              {seriesNode.logoUrl || seriesNode.symbolUrl ? (
                                <img
                                  src={seriesNode.logoUrl || seriesNode.symbolUrl}
                                  alt={seriesNode.name}
                                  className="max-w-full max-h-full object-contain drop-shadow-lg"
                                />
                              ) : (
                                <div className="text-center">
                                  <div className="text-xs font-bold text-white">{totalSets}</div>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Tooltip */}
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: isHovered ? 1 : 0 }}
                            className={`absolute left-1/2 transform -translate-x-1/2 px-3 py-2 bg-black/95 rounded-lg text-sm whitespace-nowrap z-50 pointer-events-none border border-white/20 ${
                              index % 2 === 0 ? 'top-full mt-2' : 'bottom-full mb-2'
                            }`}
                          >
                            <div className="font-semibold text-white">{seriesNode.name}</div>
                            <div className="text-white/60 text-xs">{totalSets} sets</div>
                            <div className="text-cyan-400/80 text-xs mt-1">{seriesNode.dateRange}</div>
                          </motion.div>
                        </motion.div>
                      );
                    })}
                  </div>
                  
                  {/* Connector to next year */}
                  {yearIndex < years.length - 1 && (
                    <div className="w-16 sm:w-24 h-1 mx-2 bg-gradient-to-r from-white/20 to-white/10 rounded-full" />
                  )}
                </div>
              ))}
            </div>
          </div>
        
        </div>
      </div>
    </motion.div>
  );
}

// Timeline Sets World Component - Shows sets in chronological order within a series
function TimelineSetsWorld({
  series,
  onSetClick,
  onBack,
  hoveredNode,
  setHoveredNode,
}: {
  series: SeriesNode;
  onSetClick: (set: SetNode) => void;
  onBack: () => void;
  hoveredNode: string | null;
  setHoveredNode: (id: string | null) => void;
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Sort sets by release date
  const sortedSets = useMemo(() => {
    return [...series.sets].sort((a, b) => 
      new Date(a.releaseDate).getTime() - new Date(b.releaseDate).getTime()
    ).map((set, index) => ({
      ...set,
      formattedDate: new Date(set.releaseDate).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric' 
      }),
      year: new Date(set.releaseDate).getFullYear(),
      index,
    }));
  }, [series.sets]);

  // Get unique years
  const years = useMemo(() => {
    const yearSet = new Set(sortedSets.map(s => s.year));
    return Array.from(yearSet).sort((a, b) => a - b);
  }, [sortedSets]);

  // Group sets by year
  const setsByYear = useMemo(() => {
    const groups: Record<number, typeof sortedSets> = {};
    sortedSets.forEach(s => {
      if (!groups[s.year]) groups[s.year] = [];
      groups[s.year].push(s);
    });
    return groups;
  }, [sortedSets]);

  const updateScrollButtons = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', updateScrollButtons);
      updateScrollButtons();
      return () => container.removeEventListener('scroll', updateScrollButtons);
    }
  }, [updateScrollButtons]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (scrollContainerRef.current) {
      e.preventDefault();
      const scrollMultiplier = 3;
      scrollContainerRef.current.scrollLeft += (e.deltaY + e.deltaX) * scrollMultiplier;
      updateScrollButtons();
    }
  }, [updateScrollButtons]);

  // Drag to scroll
  const [isDraggingSets, setIsDraggingSets] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [scrollStartX, setScrollStartX] = useState(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (scrollContainerRef.current) {
      setIsDraggingSets(true);
      setDragStartX(e.clientX);
      setScrollStartX(scrollContainerRef.current.scrollLeft);
      scrollContainerRef.current.style.scrollBehavior = 'auto';
    }
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDraggingSets || !scrollContainerRef.current) return;
    e.preventDefault();
    const dx = e.clientX - dragStartX;
    scrollContainerRef.current.scrollLeft = scrollStartX - dx;
    updateScrollButtons();
  }, [isDraggingSets, dragStartX, scrollStartX, updateScrollButtons]);

  const handleMouseUp = useCallback(() => {
    setIsDraggingSets(false);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.scrollBehavior = 'smooth';
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (isDraggingSets) {
      setIsDraggingSets(false);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.style.scrollBehavior = 'smooth';
      }
    }
  }, [isDraggingSets]);

  const firstYear = years[0];
  const lastYear = years[years.length - 1];

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      transition={{ duration: 0.5 }}
      className="w-full h-[calc(100vh-120px)] relative flex flex-col"
    >
      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={onBack}
        className="absolute left-4 top-4 z-30 flex items-center gap-2 px-3 py-2 bg-black/60 backdrop-blur-sm rounded-full border border-white/20 text-white/80 hover:text-white hover:bg-white/10 transition-all"
      >
        <ArrowLeft size={18} />
        <span className="text-sm">Back to Series</span>
      </motion.button>
      
      {/* Scroll Left Button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: canScrollLeft ? 1 : 0.3 }}
        onClick={() => scroll('left')}
        disabled={!canScrollLeft}
        className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/80 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all disabled:cursor-not-allowed"
      >
        <ArrowLeft size={20} />
      </motion.button>
      
      {/* Scroll Right Button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: canScrollRight ? 1 : 0.3 }}
        onClick={() => scroll('right')}
        disabled={!canScrollRight}
        className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/80 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all disabled:cursor-not-allowed rotate-180"
      >
        <ArrowLeft size={20} />
      </motion.button>
      
      {/* Bottom info bar */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 bg-black/60 backdrop-blur-sm px-4 py-2 rounded-full border border-white/10">
        <div className="flex items-center gap-2">
          {series.logoUrl && (
            <img src={series.logoUrl} alt={series.name} className="h-5 object-contain" />
          )}
          <span className="text-white text-sm font-medium">{series.name}</span>
        </div>
        <div className="w-px h-4 bg-white/20" />
        <div className="text-white/50 text-xs sm:text-sm">{sortedSets.length} sets • {firstYear === lastYear ? firstYear : `${firstYear} - ${lastYear}`}</div>
      </div>

      <div 
        ref={scrollContainerRef}
        className={`flex-1 overflow-x-auto overflow-y-hidden select-none mt-12 ${isDraggingSets ? 'cursor-grabbing' : 'cursor-grab'}`}
        style={{ scrollBehavior: 'smooth' }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <div className="min-w-max px-8 sm:px-16 py-4 h-full flex flex-col">
          {/* Timeline Container */}
          <div className="flex-1 relative">
            {/* Timeline Line */}
            <div className="absolute top-1/2 left-0 right-0 h-1 rounded-full transform -translate-y-1/2" style={{ background: `linear-gradient(to right, ${series.color}40, ${series.color}80, ${series.color}40)` }} />
            
            {/* Years and Sets */}
            <div className="flex items-center h-full gap-2 px-12">
              {years.map((year, yearIndex) => (
                <div key={year} className="flex items-center">
                  {/* Year Marker */}
                  <div className="flex flex-col items-center mr-3">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: yearIndex * 0.1 }}
                      className="relative"
                    >
                      <div className="w-3 h-3 rounded-full shadow-lg" style={{ backgroundColor: series.color, boxShadow: `0 0 10px ${series.color}` }} />
                      <div className="absolute -top-7 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                        <span className="font-bold text-sm" style={{ color: series.color }}>{year}</span>
                      </div>
                    </motion.div>
                  </div>
                  
                  {/* Sets for this year */}
                  <div className="flex items-center gap-3">
                    {setsByYear[year]?.map((set, index) => {
                      const isHovered = hoveredNode === set.id;
                      
                      return (
                        <motion.div
                          key={set.id}
                          initial={{ opacity: 0, scale: 0, y: index % 2 === 0 ? -30 : 30 }}
                          animate={{ 
                            opacity: 1, 
                            scale: isHovered ? 1.1 : 1,
                            y: index % 2 === 0 ? -50 : 50,
                          }}
                          transition={{ 
                            delay: yearIndex * 0.05 + index * 0.02,
                            type: 'spring',
                            stiffness: 100,
                            damping: 15,
                          }}
                          className="relative cursor-pointer group"
                          onClick={() => onSetClick(set)}
                          onMouseEnter={() => setHoveredNode(set.id)}
                          onMouseLeave={() => setHoveredNode(null)}
                        >
                          {/* Connection line */}
                          <div 
                            className={`absolute left-1/2 w-px ${
                              index % 2 === 0 
                                ? 'top-full h-10' 
                                : 'bottom-full h-10'
                            }`}
                            style={{ 
                              transform: 'translateX(-50%)',
                              background: index % 2 === 0 
                                ? `linear-gradient(to bottom, transparent, ${series.color}50)` 
                                : `linear-gradient(to top, transparent, ${series.color}50)`
                            }}
                          />
                          
                          {/* Set Circle */}
                          <div
                            className={`w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 overflow-hidden relative ${
                              isHovered ? 'shadow-2xl ring-2 ring-white' : ''
                            }`}
                            style={{
                              border: `2px solid ${series.color}`,
                            }}
                          >
                            <div 
                              className="absolute inset-0 rounded-full"
                              style={{
                                background: set.images?.logo 
                                  ? `radial-gradient(circle, ${series.color}44, ${series.color}22), #000`
                                  : `radial-gradient(circle, ${series.color}, ${series.color}aa)`,
                              }}
                            />
                            <div className="relative z-10 w-full h-full flex items-center justify-center p-2">
                              {set.images?.logo ? (
                                <img
                                  src={set.images.logo}
                                  alt={set.name}
                                  className="max-w-full max-h-full object-contain drop-shadow-lg"
                                />
                              ) : (
                                <div className="text-center px-1">
                                  <div className="text-[10px] font-bold text-white leading-tight">{set.name}</div>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Tooltip */}
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: isHovered ? 1 : 0 }}
                            className={`absolute left-1/2 transform -translate-x-1/2 px-3 py-2 bg-black/95 rounded-lg text-sm whitespace-nowrap z-50 pointer-events-none border border-white/20 ${
                              index % 2 === 0 ? 'top-full mt-2' : 'bottom-full mb-2'
                            }`}
                          >
                            <div className="font-semibold text-white">{set.name}</div>
                            <div className="text-white/60 text-xs">{set.total} cards</div>
                            <div className="text-xs mt-1" style={{ color: series.color }}>{set.formattedDate}</div>
                          </motion.div>
                        </motion.div>
                      );
                    })}
                  </div>
                  
                  {/* Connector to next year */}
                  {yearIndex < years.length - 1 && (
                    <div className="w-12 sm:w-16 h-1 mx-2 rounded-full" style={{ background: `linear-gradient(to right, ${series.color}30, ${series.color}10)` }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Sets World Component
function SetsWorld({
  series,
  onSetClick,
  onBack,
  hoveredNode,
  setHoveredNode,
  dragOffset,
  zoom,
}: {
  series: SeriesNode;
  onSetClick: (set: SetNode) => void;
  onBack: () => void;
  hoveredNode: string | null;
  setHoveredNode: (id: string | null) => void;
  dragOffset: { x: number; y: number };
  zoom: number;
}) {
  const containerSize = typeof window !== 'undefined' 
    ? (() => {
        // On desktop (lg), account for sidebar width (256px)
        const availableWidth = window.innerWidth >= 1024 
          ? window.innerWidth - 256 
          : window.innerWidth;
        const availableHeight = window.innerHeight - 120; // Account for header
        return Math.min(availableWidth * 0.98, availableHeight * 0.95);
      })()
    : 1000;
  const centerLogoSize = 180; // Size of the center series logo
  const minRadius = 200; // Minimum radius from center
  const radiusStep = 160; // Distance between rings
  const itemSize = 100; // Size of set circles
  const itemSpacing = 25; // Space between items
  
  // Calculate how many items fit in each ring based on its circumference
  const calculateItemsPerRing = (radius: number) => {
    const circumference = 2 * Math.PI * radius;
    const itemArcLength = itemSize + itemSpacing;
    return Math.floor(circumference / itemArcLength);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 1.2 }}
      transition={{ duration: 0.6 }}
      className="relative w-full h-[calc(100vh-120px)] flex items-center justify-center"
    >
      <div 
        className="relative" 
        style={{ 
          width: containerSize, 
          height: containerSize,
          transform: `translate(${dragOffset.x}px, ${dragOffset.y}px) scale(${zoom})`,
          transition: 'transform 0.1s ease-out',
        }}
      >
        {/* Connection Lines */}
        <svg className="absolute inset-0 pointer-events-none z-0" style={{ overflow: 'visible' }}>
          <g transform={`translate(${containerSize / 2}, ${containerSize / 2})`}>
            {(() => {
              let itemIndex = 0;
              let currentRing = 0;
              const lines = [];
              
              while (itemIndex < series.sets.length) {
                const ringRadius = minRadius + (currentRing * radiusStep);
                const itemsInThisRing = calculateItemsPerRing(ringRadius);
                
                for (let i = 0; i < itemsInThisRing && itemIndex < series.sets.length; i++) {
                  const totalInRing = Math.min(itemsInThisRing, series.sets.length - itemIndex + i);
                  const angle = (i / totalInRing) * Math.PI * 2 - Math.PI / 2;
                  const x = Math.cos(angle) * ringRadius;
                  const y = Math.sin(angle) * ringRadius;
                  
                  lines.push(
                    <line
                      key={`line-${itemIndex}`}
                      x1={0}
                      y1={0}
                      x2={x}
                      y2={y}
                      stroke={`${series.color}33`}
                      strokeWidth="1"
                    />
                  );
                  itemIndex++;
                }
                currentRing++;
              }
              
              return lines;
            })()}
          </g>
        </svg>

        {/* Sets Circles */}
        {(() => {
          let itemIndex = 0;
          let currentRing = 0;
          const ringData: Array<{ ring: number; posInRing: number; totalInRing: number }> = [];
          
          // Pre-calculate which ring each set belongs to
          while (itemIndex < series.sets.length) {
            const ringRadius = minRadius + (currentRing * radiusStep);
            const itemsInThisRing = calculateItemsPerRing(ringRadius);
            
            for (let i = 0; i < itemsInThisRing && itemIndex < series.sets.length; i++) {
              ringData.push({
                ring: currentRing,
                posInRing: i,
                totalInRing: Math.min(itemsInThisRing, series.sets.length - itemIndex + i)
              });
              itemIndex++;
            }
            currentRing++;
          }
          
          return series.sets.map((set, index) => {
            const { ring, posInRing, totalInRing } = ringData[index];
            
            // Calculate radius for this ring
            const ringRadius = minRadius + (ring * radiusStep);
            
            // Calculate angle for position in circle
            const angle = (posInRing / totalInRing) * Math.PI * 2 - Math.PI / 2;
            const pos = {
              x: Math.cos(angle) * ringRadius,
              y: Math.sin(angle) * ringRadius,
            };
            
            const isHovered = hoveredNode === set.id;
            const size = 100; // Fixed size for all set circles

          return (
            <motion.div
              key={set.id}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ 
                opacity: 1, 
                scale: isHovered ? 1.3 : 1,
                x: pos.x,
                y: pos.y,
                zIndex: isHovered ? 100 : 10,
              }}
              transition={{ 
                delay: index * 0.03,
                type: 'spring',
                stiffness: 100,
                damping: 15,
              }}
              className="absolute cursor-pointer"
              style={{
                left: '50%',
                top: '50%',
                width: size,
                height: size,
                marginLeft: -size / 2,
                marginTop: -size / 2,
              }}
              onClick={() => onSetClick(set)}
              onMouseEnter={() => setHoveredNode(set.id)}
              onMouseLeave={() => setHoveredNode(null)}
            >
              <div
                className="w-full h-full rounded-full flex items-center justify-center shadow-lg hover:shadow-2xl transition-shadow overflow-hidden relative"
                style={{
                  border: isHovered ? `3px solid ${series.color}` : `2px solid ${series.color}88`,
                  backdropFilter: 'blur(4px)',
                }}
              >
                {/* Solid background to cover lines */}
                <div 
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: set.images?.symbol || set.images?.logo
                      ? 'radial-gradient(circle, rgba(255,255,255,0.15), rgba(255,255,255,0.05)), #000'
                      : `radial-gradient(circle, ${series.color}44, ${series.color}22), #000`,
                  }}
                />
                <div className="relative z-10 w-full h-full flex items-center justify-center">
                {set.images?.logo || set.images?.symbol ? (
                  <div className="relative w-full h-full flex items-center justify-center p-3">
                    <img 
                      src={set.images.logo || set.images.symbol} 
                      alt={set.name}
                      className="max-w-full max-h-full object-contain drop-shadow-lg"
                      style={{
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                      }}
                    />
                  </div>
                ) : (
                  <div className="text-center p-2">
                    <div className="text-xs font-bold text-white drop-shadow-lg">
                      {set.total}
                    </div>
                  </div>
                )}
                </div>
              </div>
              
              {/* Tooltip */}
              {isHovered && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-black/90 rounded-lg text-sm whitespace-nowrap z-50 pointer-events-none max-w-xs"
                >
                  <div className="font-semibold truncate">{set.name}</div>
                  <div className="text-white/60 text-xs">{set.total} cards</div>
                  <div className="text-white/40 text-xs">{new Date(set.releaseDate).getFullYear()}</div>
                </motion.div>
              )}
            </motion.div>
          );
        })})()}

        {/* Center Series Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, type: 'spring', stiffness: 100, damping: 15 }}
          className="absolute left-1/2 top-1/2 z-20 cursor-pointer group"
          style={{
            width: centerLogoSize,
            height: centerLogoSize,
            marginLeft: -centerLogoSize / 2,
            marginTop: -centerLogoSize / 2,
          }}
          onClick={onBack}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          {/* Back indicator - shows on mobile */}
          <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 sm:hidden">
            <ArrowLeft size={18} className="text-white/40 animate-pulse" />
          </div>
          <div 
            className="w-full h-full rounded-full backdrop-blur-md flex items-center justify-center shadow-2xl overflow-hidden relative group-hover:ring-2 group-hover:ring-white/30 transition-all"
            style={{
              border: `3px solid ${series.color}`,
            }}
          >
            {/* Solid background to cover lines */}
            <div 
              className="absolute inset-0 rounded-full"
              style={{
                background: series.logoUrl || series.symbolUrl
                  ? `radial-gradient(circle, ${series.color}44, ${series.color}22), #000`
                  : `radial-gradient(circle, ${series.color}, ${series.color}aa)`,
              }}
            />
            <div className="relative z-10 w-full h-full flex items-center justify-center">
            {series.logoUrl || series.symbolUrl ? (
              <div className="relative w-full h-full flex items-center justify-center p-4">
                <img
                  src={series.logoUrl || series.symbolUrl}
                  alt={series.name}
                  className="max-w-full max-h-full object-contain drop-shadow-lg"
                  style={{
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                  }}
                />
              </div>
            ) : (
              <div className="text-center">
                <div className="text-sm font-bold text-white drop-shadow-lg">
                  {series.name}
                </div>
              </div>
            )}
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

// Cards World Component
function CardsWorld({
  cards,
  onCardClick,
  onBack,
  hoveredNode,
  setHoveredNode,
  dragOffset,
  zoom,
}: {
  cards: Card[];
  onCardClick: (card: Card) => void;
  onBack: () => void;
  hoveredNode: string | null;
  setHoveredNode: (id: string | null) => void;
  dragOffset: { x: number; y: number };
  zoom: number;
}) {
  // Calculate optimal circle arrangement
  const minRadius = 140; // Minimum radius from center
  const radiusStep = 150; // Distance between rings
  const cardWidth = 90;
  const cardSpacing = 20; // Space between cards
  const centerLogoSize = 160;
  
  // Calculate how many cards fit in each ring based on its circumference
  const calculateCardsPerRing = (radius: number) => {
    const circumference = 2 * Math.PI * radius;
    const cardArcLength = cardWidth + cardSpacing;
    return Math.floor(circumference / cardArcLength);
  };
  
  // Calculate the required container size based on number of cards
  const calculateRequiredSize = (cardCount: number) => {
    let remaining = cardCount;
    let ring = 0;
    while (remaining > 0) {
      const ringRadius = minRadius + (ring * radiusStep);
      const cardsInRing = calculateCardsPerRing(ringRadius);
      remaining -= cardsInRing;
      ring++;
    }
    // Return the diameter of the outermost ring plus some padding
    const outerRadius = minRadius + ((ring) * radiusStep);
    return outerRadius * 2 + 200; // Extra padding for cards
  };
  
  // Container size should accommodate all cards
  const requiredSize = calculateRequiredSize(cards.length);
  const windowSize = typeof window !== 'undefined' 
    ? Math.min(window.innerWidth, window.innerHeight) * 0.95 
    : 600;
  
  // Use the larger of required size or window size (user can zoom/drag)
  const containerSize = Math.max(requiredSize, windowSize);
  
  // Get set info from first card
  const setInfo = cards[0]?.set;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 1.2 }}
      transition={{ duration: 0.6 }}
      className="relative w-full h-[calc(100vh-120px)] flex items-center justify-center"
    >
      <div 
        className="relative" 
        style={{ 
          width: containerSize, 
          height: containerSize,
          transform: `translate(${dragOffset.x}px, ${dragOffset.y}px) scale(${zoom})`,
          transition: 'transform 0.1s ease-out',
        }}
      >
        {/* Connection Lines */}
        <svg className="absolute inset-0 pointer-events-none z-0" style={{ overflow: 'visible' }}>
          <g transform={`translate(${containerSize / 2}, ${containerSize / 2})`}>
            {(() => {
              let cardIndex = 0;
              let currentRing = 0;
              const lines = [];
              
              while (cardIndex < cards.length) {
                const ringRadius = minRadius + (currentRing * radiusStep);
                const cardsInThisRing = calculateCardsPerRing(ringRadius);
                
                for (let i = 0; i < cardsInThisRing && cardIndex < cards.length; i++) {
                  const totalInRing = Math.min(cardsInThisRing, cards.length - cardIndex + i);
                  const angle = (i / totalInRing) * Math.PI * 2 - Math.PI / 2;
                  const x = Math.cos(angle) * ringRadius;
                  const y = Math.sin(angle) * ringRadius;
                  
                  lines.push(
                    <line
                      key={`line-${cardIndex}`}
                      x1={0}
                      y1={0}
                      x2={x}
                      y2={y}
                      stroke="rgba(255, 255, 255, 0.08)"
                      strokeWidth="0.5"
                    />
                  );
                  cardIndex++;
                }
                currentRing++;
              }
              
              return lines;
            })()}
          </g>
        </svg>

        {/* Cards in Circular Arrangement */}
        {(() => {
          let cardIndex = 0;
          let currentRing = 0;
          const ringData: Array<{ ring: number; posInRing: number; totalInRing: number }> = [];
          
          // Pre-calculate which ring each card belongs to
          while (cardIndex < cards.length) {
            const ringRadius = minRadius + (currentRing * radiusStep);
            const cardsInThisRing = calculateCardsPerRing(ringRadius);
            
            for (let i = 0; i < cardsInThisRing && cardIndex < cards.length; i++) {
              ringData.push({
                ring: currentRing,
                posInRing: i,
                totalInRing: Math.min(cardsInThisRing, cards.length - cardIndex + i)
              });
              cardIndex++;
            }
            currentRing++;
          }
          
          return cards.map((card, index) => {
            const { ring, posInRing, totalInRing } = ringData[index];
            
            // Calculate radius for this ring
            const ringRadius = minRadius + (ring * radiusStep);
            
            // Calculate angle for position in circle - evenly distributed
            const angle = (posInRing / totalInRing) * Math.PI * 2 - Math.PI / 2; // Start from top
            const pos = {
              x: Math.cos(angle) * ringRadius,
              y: Math.sin(angle) * ringRadius,
            };
            
            const isHovered = hoveredNode === card.id;
            const cardHeight = 126; // Aspect ratio 2.5:3.5

          return (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ 
                opacity: 1, 
                scale: isHovered ? 1.5 : 1,
                x: pos.x,
                y: pos.y,
                zIndex: isHovered ? 100 : 10,
              }}
              transition={{ 
                delay: index * 0.005,
                type: 'spring',
                stiffness: 100,
                damping: 15,
              }}
              className="absolute cursor-pointer"
              style={{
                left: '50%',
                top: '50%',
                width: cardWidth,
                height: cardHeight,
                marginLeft: -cardWidth / 2,
                marginTop: -cardHeight / 2,
                perspective: '600px',
              }}
              onClick={() => onCardClick(card)}
              onMouseEnter={() => setHoveredNode(card.id)}
              onMouseLeave={() => setHoveredNode(null)}
            >
              <div 
                className="w-full h-full rounded-lg relative group"
                style={{
                  transformStyle: 'preserve-3d',
                  transition: 'transform 0.1s ease-out',
                  // Card thickness using box-shadow (much more performant than multiple layers)
                  boxShadow: isHovered 
                    ? '0 0 0 1px #c9a227, 0 2px 0 0 #8b6914, 0 4px 0 0 #5c4a1f, 0 6px 15px rgba(0,0,0,0.5)'
                    : '0 0 0 1px rgba(255,255,255,0.1), 0 2px 0 0 #5c4a1f, 0 4px 8px rgba(0,0,0,0.3)',
                }}
                onMouseMove={isHovered ? (e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const y = e.clientY - rect.top;
                  const centerX = rect.width / 2;
                  const centerY = rect.height / 2;
                  const rotateX = (y - centerY) / 6;
                  const rotateY = (centerX - x) / 6;
                  e.currentTarget.style.transform = `rotateX(${-rotateX}deg) rotateY(${-rotateY}deg)`;
                } : undefined}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'rotateX(0deg) rotateY(0deg)';
                }}
              >
                {/* Card front face */}
                <div className="absolute inset-0 rounded-lg overflow-hidden bg-black">
                  {card.images?.small ? (
                    <img
                      src={card.images.small}
                      alt={card.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        const currentSrc = target.src;
                        
                        const pngBucketUrl = `https://yjzlookljahphgxcwpnv.supabase.co/storage/v1/object/public/pokemon-images/pokemon_cards/${card.id}_small.png`;
                        const officialApiUrl = `https://images.pokemontcg.io/${card.id.split('-')[0]}/${card.number}.png`;
                        
                        if (currentSrc.includes('pokemon-images-webp')) {
                          target.src = pngBucketUrl;
                        } else if (currentSrc.includes('pokemon-images/') && !currentSrc.includes('pokemontcg.io')) {
                          target.src = officialApiUrl;
                        } else {
                          target.style.display = 'none';
                          target.nextElementSibling?.classList.remove('hidden');
                        }
                      }}
                    />
                  ) : null}
                  <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br from-white/10 to-white/5 ${card.images?.small ? 'hidden' : ''}`}>
                    <span className="text-white/40 text-xs text-center p-1">{card.name}</span>
                  </div>
                  
                  {/* Shine effect - only on hovered card */}
                  {isHovered && (
                    <div 
                      className="absolute inset-0 pointer-events-none rounded-lg opacity-50"
                      style={{
                        background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.5) 45%, transparent 55%)',
                        backgroundSize: '200% 200%',
                        animation: 'shine 1.5s ease-in-out infinite',
                      }}
                    />
                  )}
                  
                  {/* Border */}
                  <div className={`absolute inset-0 rounded-lg border-2 pointer-events-none transition-colors ${isHovered ? 'border-white/50' : 'border-white/20'}`}></div>
                </div>
              </div>
              
              {/* Tooltip */}
              {isHovered && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-black/90 rounded-lg text-sm whitespace-nowrap z-50 pointer-events-none"
                >
                  <div className="font-semibold">{card.name}</div>
                  <div className="text-white/60 text-xs">#{card.number}</div>
                  {card.rarity && (
                    <div className="text-white/40 text-xs">{card.rarity}</div>
                  )}
                </motion.div>
              )}
            </motion.div>
          );
        })})()}

        {/* Center Set Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, type: 'spring', stiffness: 100, damping: 15 }}
          className="absolute left-1/2 top-1/2 z-20 cursor-pointer group"
          style={{
            width: centerLogoSize,
            height: centerLogoSize,
            marginLeft: -centerLogoSize / 2,
            marginTop: -centerLogoSize / 2,
          }}
          onClick={onBack}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          {/* Back indicator - shows on mobile */}
          <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 sm:hidden">
            <ArrowLeft size={18} className="text-white/40 animate-pulse" />
          </div>
          <div className="w-full h-full rounded-full flex items-center justify-center shadow-2xl border-2 border-white/20 overflow-hidden relative group-hover:ring-2 group-hover:ring-white/30 transition-all">
            {/* Solid background to cover lines */}
            <div className="absolute inset-0 rounded-full bg-black"></div>
            <div className="absolute inset-0 rounded-full bg-white/10 backdrop-blur-md"></div>
            <div className="relative z-10 w-full h-full flex items-center justify-center">
            {setInfo?.images?.logo || setInfo?.images?.symbol ? (
              <img
                src={setInfo.images.logo || setInfo.images.symbol}
                alt={setInfo.name}
                className="w-4/5 h-4/5 object-contain drop-shadow-lg"
                style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                }}
              />
            ) : (
              <div className="text-center p-2">
                <div className="text-xs font-bold text-white drop-shadow-lg">
                  {setInfo?.name}
                </div>
              </div>
            )}
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

